import { Button, Form, Input, Switch } from 'antd';
import { Formik, FormikConfig, FormikProps } from 'formik';
import useCrud from 'hooks/useCrud';
import { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { pauseTaskList } from '../service';
import { useMount } from 'ahooks';
import FormField from 'components/FormField';
import useModal from 'hooks/useModal';
import { StoreScopesModal, getStoreCount } from 'components/StoreScopes';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { Content } from 'views/Aide/Scenario/TaskForm/StoreScopesField';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';

type FV = Util.PartialPartial<
  Pick<Aide.Pause.Task, 'id' | 'name' | 'status' | 'platformStoreScopes'>,
  'id'
>;

export interface TaskFormProps extends Util.PartialPartial<FormikConfig<FV>, 'initialValues'> {
  readonly?: boolean;
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
}

const defaultInitialValues: FV = {
  name: '',
  status: 'IN_VALID',
  platformStoreScopes: [],
};

const nameLimitLength = 30;

function TaskForm(props: TaskFormProps) {
  const { readonly = false, children, initialValues = defaultInitialValues, ...otherProps } = props;

  const { t } = useTranslate();

  const { whiteListConfig: { AIDE_REMOVE_TASK } = {} } = useOrgConfigStateValue();

  const { list, fetchList } = useCrud({
    readList: pauseTaskList,
  });

  useMount(() => {
    fetchList().catch(() => {});
  });

  const { visible, show, hide } = useModal();

  return (
    <Formik enableReinitialize initialValues={initialValues} {...otherProps}>
      {(props) => {
        const name = (
          <FormField<FV['name']>
            required
            name="name"
            label={AIDE_REMOVE_TASK ? t('marketing.策略名称') : t('marketing.任务名称')}
            validate={(value) => {
              if (!value || !value.trim()) {
                return AIDE_REMOVE_TASK
                  ? t('marketing.请输入策略名称')
                  : t('marketing.请输入任务名称');
              }

              if (list?.some((_) => _.name === value && _.id !== initialValues.id)) {
                return AIDE_REMOVE_TASK
                  ? t('marketing.策略名称已存在')
                  : t('marketing.任务名称已存在');
              }
            }}
          >
            {({ field }) => {
              return (
                <Input
                  disabled={readonly}
                  placeholder={
                    AIDE_REMOVE_TASK ? t('marketing.请输入策略名称') : t('marketing.请输入任务名称')
                  }
                  showCount
                  maxLength={nameLimitLength}
                  {...field}
                  style={{ width: '570px' }}
                />
              );
            }}
          </FormField>
        );

        const platformStoreScopes = (
          <FormField<FV['platformStoreScopes']>
            required
            name="platformStoreScopes"
            label={t('aide.应用店铺')}
            validate={(value) => {
              const storeCount = getStoreCount(value);

              return storeCount === 0 ? t('aide.请选择应用店铺') : undefined;
            }}
          >
            {({ field: { value, onChange } }) => {
              const modal = (
                <StoreScopesModal
                  permId={permissionNameIdMap['配置暂停自动回复']}
                  visible={visible}
                  supportWa={false}
                  showFooterTips={false}
                  onCancel={hide}
                  value={value}
                  onChange={onChange}
                />
              );

              const render = (contentText: ReactNode, editText: ReactNode) => {
                return (
                  <Content>
                    {contentText && <div>{contentText}</div>}
                    <Button disabled={readonly} type="link" onClick={show}>
                      {editText}
                    </Button>
                    {modal}
                  </Content>
                );
              };

              const storeCount = getStoreCount(value);

              if (storeCount === 0) {
                return render(null, t('remind.选择店铺'));
              }

              return render(<PopoverPreview value={value} />, t('phrase.重新选择'));
            }}
          </FormField>
        );

        const status = (
          <FormField<FV['status']>
            required
            name="status"
            label={AIDE_REMOVE_TASK ? t('aide.策略开关') : t('aide.任务开关')}
          >
            {({ field: { value, onChange } }) => {
              return (
                <Switch
                  disabled={readonly}
                  checked={value === 'VALID'}
                  onChange={(checked) => onChange(checked ? 'VALID' : 'IN_VALID')}
                  checkedChildren={t('aide.开')}
                  unCheckedChildren={t('aide.关')}
                />
              );
            }}
          </FormField>
        );

        const form = (
          <Form>
            {name}
            {platformStoreScopes}
            {status}
          </Form>
        );

        return children(form, props);
      }}
    </Formik>
  );
}

export default TaskForm;
